import React, { useState } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement, 
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement
} from 'chart.js';
import spinner from './spinner.gif';
import './css/reset.css';
import './css/SalesPerform.css';

// 차트 컴포넌트 import

import SalesDateBarChart from './components/SalesDate_BarChart';
import C1DrilldownPieChart from './components/C1_DrilldownPieChart';
import C2DrilldownBarChart from './components/C2_DrilldownBarChart';
import C3DrilldownPieChart from './components/C3_DrilldownPieChart';
import ProductTop20BarChart from './components/ProductTop20_BarChart';
import CustomerSalesHistogram from './components/Customer_BarChart';
import C1SMonthlyLineChart from './components/C1SMonthly_LineChart';
import C2SMonthlyLineChart from './components/C2SMonthly_LineChart';
import C3SMonthlyLineChart from './components/C3SMonthly_LineChart';
import MonthlyOrderSalesLineChart from './components/OrderSalesMonthly_LineChart';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend, ArcElement);

// 차트 색상 팔레트
const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#a28fd0', '#f7b267', '#f4845f', '#43bccd', '#b5d99c', '#e05d5d', '#f6ab6c', '#6a89cc', '#38ada9', '#b8e994'];

// 데이터 타입 정의
interface PerformanceRecord {
  c1D: string; // 1차대분류
  c1J: string; // 1차중분류
  c1S: string; // 1차소분류
  c2D: string; // 2차대분류
  c2J: string; // 2차중분류
  c2S: string; // 2차소분류
  c3D: string; // 3차대분류
  c3J: string; // 3차중분류
  c3S: string; // 3차소분류
  c4: string;  // 날짜
  c5N: string;
  c6N: string;
  c7: string;  // 고객코드
  c8: string;
  c9: string;  // 상품코드
  c10: string; // 상품명
  c11: string;
  c17: string;
  c24: number; // 수량
  c26: number; // 금액(매출액)
  c27: number;
  c28: number;
  c29: number;
  c36: string;
  c39: string;
  c41: string;
  c42: string; // 고객명
  c43: string; // 연락처
  c44: string; // 주소
}

const SalesPerform: React.FC = () => {
  const [C2S, setC2S] = useState<string>(''); // 팀
  const [C9, setC9] = useState<string>('');   // 상품코드
  const [C10, setC10] = useState<string>(''); // 상품명
  const [C7, setC7] = useState<string>('');   // 고객코드
  const [records, setRecords] = useState<PerformanceRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // 검색
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload: any = {};
      if (C2S) payload.C2S = C2S;
      if (C9) payload.C9 = C9;
      if (C10) payload.C10 = C10;
      if (C7) payload.C7 = C7;

      const response = await axios.post<PerformanceRecord[]>(
        'https://mongodbapi.dahaeinc.co.kr/Mongo/SearchSalesPerformFields',
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      setRecords(response.data);
    } catch (err: any) {
      console.error(err);
      setError('데이터를 가져오는 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  // BI 지표
  const totalRecords = records.length; // 총 건수
  const totalQty = records.reduce((sum, rec) => sum + rec.c24, 0); // 총 수량
  const totalRevenue = records.reduce((sum, rec) => sum + rec.c26, 0); // 총 매출액

  // ------------------- 렌더링 -------------------
  return (
    <div className="sales-perform">
      <h1 className="page-title">매출 실적 대시보드</h1>
      {/* 검색 폼 */}
      <form onSubmit={handleSubmit} className="search-form">
        {[
          { label: '팀 (C2S)', value: C2S, setter: setC2S, placeholder: '' },
          { label: '상품 코드 (C9)', value: C9, setter: setC9, placeholder: '품목SPEC' },
          { label: '상품명 (C10)', value: C10, setter: setC10, placeholder: '품명' },
          { label: '고객 코드 (C7)', value: C7, setter: setC7, placeholder: '거래처코드' }
        ].map((field, idx) => (
          <div key={idx}>
            <label className="search-label">{field.label}</label>
            <input
              type="text"
              value={field.value}
              onChange={(e) => field.setter(e.target.value)}
              className="search-input"
              placeholder={field.placeholder}
            />
          </div>
        ))}
        <div>
          <button
            type="submit"
            className={loading ? "search-button disable" : "search-button"}
            disabled={loading}
          >
            {loading && <img src={spinner} alt="로딩중" className="spinner"/>}
            {loading ? '검색중...' : '검색'}
          </button>
        </div>
      </form>
      {/* 요약 카드 */}
      <div className="summary">
        <div>
          <h2>총 건수</h2>
          <p>{totalRecords.toLocaleString()}</p>
        </div>
        <div>
          <h2>총 수량</h2>
          <p>{totalQty.toLocaleString()}</p>
        </div>
        <div>
          <h2>총 매출액</h2>
          <p>
            {totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 원
          </p>
        </div>
      </div>

      <ul className='chart-container'>
        {/* 일/월별 매출 차트 */}
        <li className="chart-box">
          <h2>일/월별 매출 추이</h2>
          {records.length > 0 ? (
            <SalesDateBarChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 1차 소분류별 월별 매출 추이 */}
        <li className="chart-box">
          <h2>1차 소분류별 월별 매출 추이</h2>
          {records.length > 0 ? (
            <C1SMonthlyLineChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 2차 소분류별 월별 매출 추이 */}
        <li className="chart-box">
          <h2>2차 소분류별 월별 매출 추이</h2>
          {records.length > 0 ? (
            <C2SMonthlyLineChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 3차 소분류별 월별 매출 추이 */}
        <li className="chart-box">
          <h2>3차 소분류별 월별 매출 추이</h2>
          {records.length > 0 ? (
            <C3SMonthlyLineChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 1차 분류 드릴다운 파이 차트 */}
        <li className="chart-box">
          <h2>1차 분류 드릴다운 파이 차트</h2>
          {records.length > 0 ? (
            <C1DrilldownPieChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 2차 분류 드릴다운 막대 차트 */}
        <li className="chart-box">
          <h2>2차 분류 드릴다운 막대 차트</h2>
          {records.length > 0 ? (
            <C2DrilldownBarChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 3차 분류 드릴다운 파이 차트 */}
        <li className="chart-box">
          <h2>3차 분류 드릴다운 파이 차트</h2>
          {records.length > 0 ? (
            <C3DrilldownPieChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 상품명별 매출 TOP 20 수평 막대 그래프 */}
        <li className="chart-box">
          <h2>상품별 매출 TOP 20</h2>
          {records.length > 0 ? (
            <ProductTop20BarChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 고객별 총 매출액 막대 차트 */}
        <li className="chart-box">
          <h2>고객별 총 매출액 막대 차트</h2>
          {records.length > 0 ? (
            <CustomerSalesHistogram records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
        {/* 월별 주문수/매출액(2축) 선형 그래프 */}
        <li className="chart-box">
          <h2>월별 주문수/매출액(2축)</h2>
          {records.length > 0 ? (
            <MonthlyOrderSalesLineChart records={records} colors={colors} />
          ) : (
            <p className="no-data">표시할 데이터가 없습니다.</p>
          )}
        </li>
      </ul>
      {/* 상세 내역 */}
      <div className="detail-container">
        {!showDetails ? (
          <button
            className="detail-button"
            onClick={() => setShowDetails(true)}
          >
            상세내역 보기
          </button>
        ) : (
          records.length > 0 ? (
            <div className="detail">
              <h2>상세 내역</h2>
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>1차<br/>대분류</th>
                    <th>1차<br/>중분류</th>
                    <th>1차<br/>소분류</th>
                    <th>2차<br/>대분류</th>
                    <th>2차<br/>중분류</th>
                    <th>2차<br/>소분류</th>
                    <th>3차<br/>대분류</th>
                    <th>3차<br/>중분류</th>
                    <th>3차<br/>소분류</th>
                    <th>날짜</th>
                    <th>팀</th>
                    <th>주문 번호</th>
                    <th>상품 코드</th>
                    <th>상품명</th>
                    <th>고객 코드</th>
                    <th>수량</th>
                    <th>금액</th>
                    <th>고객</th>
                    <th>연락처</th>
                    <th>주소</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((rec, idx) => (
                    <tr key={idx}>
                      <td>{rec.c1D}</td>
                      <td>{rec.c1J}</td>
                      <td>{rec.c1S}</td>
                      <td>{rec.c2D}</td>
                      <td>{rec.c2J}</td>
                      <td>{rec.c2S}</td>
                      <td>{rec.c3D}</td>
                      <td>{rec.c3J}</td>
                      <td>{rec.c3S}</td>
                      <td style={{textWrap:"nowrap"}}>{rec.c4}</td>
                      <td>{rec.c2J}</td>
                      <td style={{textWrap:"nowrap"}}>{rec.c7}</td>
                      <td style={{textWrap:"nowrap"}}>{rec.c9}</td>
                      <td>{rec.c10}</td>
                      <td>{rec.c17}</td>
                      <td style={{textWrap:"nowrap"}}>{rec.c24.toLocaleString()}</td>
                      <td style={{textWrap:"nowrap"}}>{rec.c26.toLocaleString()}</td>
                      <td>{rec.c42}</td>
                      <td style={{textWrap:"nowrap"}}>{rec.c43}</td>
                      <td>{rec.c44}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">검색 결과가 없습니다. 필터를 변경해 보세요.</p>
          )
        )}
      </div>
    </div>
  );
};

export default SalesPerform;